import { Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";

interface ScoreBreakdownItemProps {
  label: string;
  value: number;
  /** When true, renders the value as-is (no +/- prefix) and uses neutral colors. */
  isBaseline?: boolean;
}

function formatValue(value: number): string {
  return value > 0 ? `+${value}` : `${value}`;
}

function ScoreBreakdownItem(props: ScoreBreakdownItemProps): JSX.Element {
  const { label, value, isBaseline = false } = props;
  const isNegative = value < 0;
  const isPositive = value > 0;

  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ paddingY: 2 }}>
      <Text
        variant="body2"
        color={(theme) => {
          if (isBaseline) {
            return theme.palette.text.secondary;
          }
          if (isNegative) {
            return theme.palette.intent?.error.text;
          }
          if (isPositive) {
            return theme.palette.success.main;
          }
          return theme.palette.text.secondary;
        }}
      >
        {label}
      </Text>
      <Text
        variant="body1"
        color={(theme) => {
          if (isBaseline) {
            return theme.palette.text.primary;
          }
          if (isNegative) {
            return theme.palette.intent?.error.text;
          }
          if (isPositive) {
            return theme.palette.success.main;
          }
          return theme.palette.text.primary;
        }}
      >
        {isBaseline ? value : formatValue(value)}
      </Text>
    </Stack>
  );
}

interface ScoreBreakdownListProps {
  currentScore: number;
  baseScoreChange: number;
  policyLabel: string;
  creditBenefit?: number;
}

export function ScoreBreakdownList(props: ScoreBreakdownListProps): JSX.Element {
  const { currentScore, baseScoreChange, policyLabel, creditBenefit } = props;

  const hasScoreChange = baseScoreChange !== 0;
  const hasCreditBenefit = isDefined(creditBenefit) && creditBenefit > 0;

  return (
    <Stack sx={{ paddingX: 6, paddingY: 4 }}>
      <ScoreBreakdownItem isBaseline label="Current Clipboard Score" value={currentScore} />
      {hasScoreChange && <ScoreBreakdownItem label={policyLabel} value={baseScoreChange} />}
      {hasCreditBenefit && (
        <ScoreBreakdownItem label="Same-day cancellation credit" value={creditBenefit} />
      )}
    </Stack>
  );
}
